import { useCartStore, selectValidatedItems } from "@/lib/cart/cart-store"
import { useFees } from "@/context/fees-context"
import { useAuth } from "@/context/auth-context"
import { useCouponStore } from "@/lib/coupons/coupon-store"
import { calculateOrderTotals, getEffectivePrice } from "@/lib/cart/calculate-order-totals"
import { getResultOfApplyingCouponsToOrder } from "@/lib/coupons/calculate-coupons"
import type { FeesConfig, OrderTotals } from "@/lib/cart/types"

const DEFAULT_FEES: FeesConfig = {
  deliveryFee: 123,
  serviceFee: 12,
  expressDeliveryFee: 0,
  freeDeliveryThreshold: 800,
  freeServiceThreshold: 200,
}

function buildFeesConfig(raw: any): FeesConfig {
  if (!raw || typeof raw !== "object") return DEFAULT_FEES
  return {
    deliveryFee: raw.DEFAULT_DELIVERY_FEE ?? DEFAULT_FEES.deliveryFee,
    serviceFee: raw.SMALL_ORDER_SERVICE_FEE ?? DEFAULT_FEES.serviceFee,
    expressDeliveryFee: raw.express_delivery_fee ?? raw.expressDeliveryFee ?? DEFAULT_FEES.expressDeliveryFee,
    freeDeliveryThreshold: raw.DEFAULT_FREE_DELIVERY_MIN ?? DEFAULT_FEES.freeDeliveryThreshold,
    freeServiceThreshold: raw.MIN_ORDER_AMOUNT ?? DEFAULT_FEES.freeServiceThreshold,
  }
}

export function useOrderTotals(expressDeliveryFeeOverride?: number): OrderTotals {
  const validatedItems = useCartStore(selectValidatedItems)
  const rawFees = useFees()
  const { user } = useAuth()
  const couponDetails = useCouponStore((s) => s.couponDetails)

  const fees = buildFeesConfig(rawFees)
  const isCaliiPlusMember = !!user?.is_calii_plus_member

  const items = validatedItems.map((v) => ({
    productId: v.productId,
    price: getEffectivePrice(v),
    quantity: v.quantity,
  }))

  const allCoupons = Object.values(couponDetails)
  const couponResult = allCoupons.length > 0
    ? getResultOfApplyingCouponsToOrder(allCoupons, validatedItems, isCaliiPlusMember, fees.deliveryFee, fees.freeDeliveryThreshold)
    : null

  const couponDiscount = couponResult?.totalCouponDiscounts ?? 0
  const deliveryFeeOverride = couponResult ? couponResult.deliveryAmount : undefined

  return calculateOrderTotals({
    items,
    fees,
    accountBalance: user?.account_balance ?? 0,
    couponDiscount,
    deliveryFeeOverride,
    expressDeliveryFeeOverride,
  })
}
